import React from "react";
import {
  AbsoluteFill,
  spring,
  interpolate,
  Easing,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

/**
 * RibbonBadge
 * - Slides in from the left and fades out.
 * - Title (top/red) and subtitle (bottom/white) are modular.
 */
export const RibbonBadge = ({
  title = "OVERVIEW",
  subtitle = "Gaming at 240fps?\nYes!",
  // Timing
  startFrame = 0,
  inDuration = 18,        // frames for slide-in
  visibleForFrames = 70,  // frames to stay fully visible
  outDuration = 18,       // frames for fade-out
  // Layout
  x = 80,                 // final x position (px) from the left
  y = 60,                 // final y position (px) from the top
  width = 1200,           // badge width
  topHeight = 120,        // red strip height
  bottomHeight = 200,     // white panel height
  notch = 90,             // size of the chevron/notch on right
  // Colors
  topColor = "#e72621",
  bottomColor = "#ffffff",
  titleColor = "#ffffff",
  subtitleColor = "#111111",
  // Fonts
  titleFont = "Impact, Anton, Arial Black, system-ui, sans-serif",
  bodyFont = "Inter, system-ui, Arial, sans-serif",
}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  // ---- Progress (enter) ----
  const enter = spring({
    fps,
    frame: frame - startFrame,
    config: {damping: 200, stiffness: 200, mass: 0.7},
  }); // 0 → 1

  // ---- Progress (exit) ----
  const outStart = startFrame + inDuration + visibleForFrames;
  const opacity = interpolate(
    frame,
    [outStart, outStart + outDuration],
    [1, 0],
    {easing: Easing.out(Easing.quad), extrapolateLeft: "clamp", extrapolateRight: "clamp"}
  );

  // Slide from fully off-screen left to final x.
  // We overshoot the width a bit so it starts outside the canvas.
  const offscreenX = x - (width + 170);
  const translateX = offscreenX + (x - offscreenX) * Math.min(1, enter);

  return (
    <AbsoluteFill
      style={{
        pointerEvents: "none",
        transform: `translateX(${translateX}px) translateY(${y}px)`,
        opacity,
        filter: "drop-shadow(0 10px 24px rgba(0,0,0,0.35))",
        width,
        height: topHeight + bottomHeight,
      }}
    >
      {/* --- TOP RED CHEVRON TAB --- */}
      <div
        style={{
          height: topHeight,
          background: topColor,
          color: titleColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 48px",
          // Shape matching the provided SVG with left inset and right chevron
          clipPath: `polygon(
            0% 0%,
            100% 0%,
            calc(100% - ${notch/2}px) 50%,
            100% 100%,
            0% 100%,
            ${notch/4}px 50%
          )`,
        }}
      >
        <span
          style={{
            fontFamily: titleFont,
            fontSize: Math.round(topHeight * 0.56),
            fontWeight: 900,
            letterSpacing: 2,
            textTransform: "uppercase",
            lineHeight: 1,
          }}
        >
          {title}
        </span>
      </div>

      {/* --- BOTTOM WHITE BEVELED PANEL --- */}
      <div
        style={{
          height: bottomHeight,
          background: bottomColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 56px",
          // Extend the container width to accommodate the angled shape
          width: `calc(100% + ${notch}px)`,
          // Shape with top-right unchanged but bottom-right extending outward
          clipPath: `polygon(
            0% 0%,
            calc(100% - ${notch}px) 0%,
            100% 100%,
            7.5% 100%
          )`,
        }}
      >
        <span
          style={{
            fontFamily: bodyFont,
            fontSize: Math.round(bottomHeight * 0.34),
            fontWeight: 800,
            color: subtitleColor,
            textAlign: "center",
            lineHeight: 1.1,
            whiteSpace: "pre-line",
          }}
        >
          {subtitle}
        </span>
      </div>
    </AbsoluteFill>
  );
};

export default RibbonBadge;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
